import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type InboxEmailSummary = {
  id: string;
  resend_email_id: string | null;
  message_id: string | null;
  from: string;
  to: string[];
  cc: string[] | null;
  bcc: string[] | null;
  subject: string | null;
  preview: string | null;
  attachments: Array<Record<string, unknown>>;
  received_at: number;
  is_read: boolean;
  is_archived: boolean;
  created_at: number;
};

export type InboxEmailDetail = InboxEmailSummary & {
  text?: string | null;
  html?: string | null;
};

export type InboxTotals = { total: number; unread: number; archived: number };

export function useInboxList(
  status: 'all' | 'unread' | 'archived' = 'all',
  limit = 50,
) {
  return useQuery({
    queryKey: ['platform', 'inbox', status, limit],
    queryFn: () => {
      const qs = new URLSearchParams({ status, limit: String(limit) });
      return api.get<{ emails: InboxEmailSummary[]; totals: InboxTotals }>(
        `/api/platform/inbox?${qs.toString()}`,
      );
    },
    refetchInterval: 60_000,
  });
}

export function useInboxEmail(id: string | null) {
  return useQuery({
    queryKey: ['platform', 'inbox', 'detail', id],
    queryFn: () =>
      api.get<{ email: InboxEmailDetail; body_fetch_error: string | null }>(
        `/api/platform/inbox/${id}`,
      ),
    enabled: !!id,
  });
}

export function useUpdateInboxEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      is_read,
      is_archived,
    }: {
      id: string;
      is_read?: boolean;
      is_archived?: boolean;
    }) =>
      api.patch<{ id: string }>(`/api/platform/inbox/${id}`, {
        is_read,
        is_archived,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'inbox'] });
    },
  });
}

export function useDeleteInboxEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: boolean }>(`/api/platform/inbox/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'inbox'] });
    },
  });
}

export function useReplyInboxEmail(id: string) {
  return useMutation({
    mutationFn: (body: { to?: string; subject: string; text?: string; html?: string }) =>
      api.post<{ sent: boolean; id: string | null }>(`/api/platform/inbox/${id}/reply`, body),
  });
}
